import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { isDataOutdated } from '_stores/storeHelper';
import { SelectionModeType } from '_clients/BFFClient.generated';
import { DistributionChangeType } from '_components/molecules/CreditDispatchTable/CreditDispatchTable.types';

export type Distribution ={
  networkId: string;
  contractId: number;
  rcuId: string;
  newAllocatedBoosts?: number;
  marketId: number;
  productType: string;
  addDate: Date;
  selectionModeType?: SelectionModeType;
}

export type DistributionStore = {
  distributions: Distribution[];
  findDistribution: (networkId: string | null, contractId: number | null, rcuId: string, marketId: number, productType: string) => Distribution | undefined;
  clearAllMatchingDistributions: (networkId: string | null, contractId: number | null, marketId: number, productType: string) => void;
  clearSpecificDistribution: (networkId: string | null, contractId: number | null, marketId: number, productType: string, rcuId: string) => void;
  upsertDistributions: (value: Distribution[]) => void;
  getValidDistributions: () => Distribution[];
  getDistributionChangeType: (marketId: number, productType: string, networkId: string | null, contractId: number | null) => DistributionChangeType | null;
  filterDistributions: (marketId: number, productType: string, members: any[], networkId: string | null, contractId: number | null) => Distribution[];
  removeOutdatedDistributions: () => void;
};

export const useDistributionStore = create<DistributionStore>()(
  persist(
    (set, get) => {
      const getValidDistributions = () => {
        const previousDisttribution = get().distributions;
        const validDistributions = previousDisttribution?.filter(d => !isDataOutdated(d.addDate));
        if (validDistributions.length !== previousDisttribution.length) {
          set({ distributions: validDistributions });
        }
        return validDistributions;
      };

      return({
      distributions: [],
      findDistribution: (networkId, contractId, rcuId, marketId,  productType) => {
        const validDistributions = getValidDistributions();
        const distribution = networkId
          ? validDistributions.find(d => d.networkId == networkId && d.marketId == marketId && d.productType == productType && rcuId == d.rcuId)
          : validDistributions.find(d => d.contractId == contractId && d.marketId == marketId && d.productType == productType && rcuId == d.rcuId);
        return distribution;
      },
      upsertDistributions: (newDistributions : Distribution[]) => {
        let distributions = get().distributions;
        newDistributions.forEach(newDistribution => {
          const index = newDistribution.networkId
            ? distributions.findIndex(d => d.rcuId == newDistribution.rcuId && d.networkId == newDistribution.networkId && d.marketId == newDistribution.marketId && d.productType == newDistribution.productType)
            : distributions.findIndex(d => d.rcuId == newDistribution.rcuId && d.contractId == newDistribution.contractId && d.marketId == newDistribution.marketId && d.productType == newDistribution.productType);
          if (index !== -1) {
            distributions[index] = newDistribution;
          } else {
            distributions.push(newDistribution);
          }
        });
        set({ distributions: distributions });
      },
      clearAllMatchingDistributions: (networkId: string | null, contractId: number | null, marketId: number, productType: string) => {
        const distributions = networkId
          ? get().distributions?.filter(d => !(d.networkId == networkId && d.marketId == marketId && d.productType == productType))
          : get().distributions?.filter(d => !(d.contractId == contractId && d.marketId == marketId && d.productType == productType));
        set({ distributions: distributions });
      },
      clearSpecificDistribution: (networkId: string | null, contractId: number | null, marketId: number, productType: string, rcuId: string) => {
        const distributions = networkId
          ? get().distributions?.filter(d => !(d.networkId == networkId && d.marketId == marketId && d.productType == productType && rcuId == d.rcuId))
          : get().distributions?.filter(d => !(d.contractId == contractId && d.marketId == marketId && d.productType == productType && rcuId == d.rcuId));
        set({ distributions: distributions });;
      },
      getValidDistributions: getValidDistributions,
      getDistributionChangeType: (marketId: number, productType: string, networkId: string | null, contractId: number | null) => {
        const distributionsRetrieved = get().distributions?.filter(d => d.networkId == networkId && d.contractId == contractId &&
          d.marketId == marketId && d.productType == productType);
    
        if(distributionsRetrieved.length > 0) {
          const inProgress = distributionsRetrieved.some(d => d.newAllocatedBoosts != undefined);
          const modeChanged = distributionsRetrieved.some(d => d.selectionModeType != undefined);
          if(inProgress && modeChanged) {
            return DistributionChangeType.ALL;
          }
          else if(inProgress) {
            return DistributionChangeType.ALLOCATED_BOOST;
          }
          else if(modeChanged) {
            return DistributionChangeType.DISTRIBUTION_MODE;
          }
        }
        return null;
      }, 
      filterDistributions: (marketId, productType, members, networkId, contractId) => {
        const validDistributions = getValidDistributions();
        const distributions = networkId
          ? validDistributions.filter(d => d.networkId == networkId && d.marketId == marketId && d.productType == productType && members.some(m => m.rcuID === d.rcuId))
          : validDistributions.filter(d => d.contractId == contractId && d.marketId == marketId && d.productType == productType && members.some(m => m.rcuID === d.rcuId));
        return distributions;
      },
      removeOutdatedDistributions: () => {
        const outdatedDistributions = get().distributions?.filter(d => isDataOutdated(d.addDate));
        outdatedDistributions?.forEach(d => get().clearSpecificDistribution(d.networkId, d.contractId, d.marketId, d.productType, d.rcuId));
      }
    })},
    {
      name: 'distribution',
      storage: createJSONStorage(() => localStorage),
    }
  )
)








